import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Flame } from "lucide-react";

import { TermsDocument } from "@/components/TermsDocument";
import { currentTerms } from "@/engine/support/terms";

/**
 * The Terms of Use.
 *
 * Reachable by its address and by nothing else: while the document is a draft
 * nothing in the application links here, so the page exists to be read by
 * whoever is given the link. The entry screen and the settings menu gain their
 * links when the text is approved (#214).
 *
 * The document is English only and deliberately not translated — the English
 * version is the authoritative one, and a legal text machine-translated into
 * every locale the app carries is a liability. Only the chrome around it speaks
 * the visitor's language.
 */

const TITLE = "Terms of Use — SyncAni";
const DESCRIPTION = "The terms you agree to when you use SyncAni.";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      // A draft must not be indexed, and there is no approved version to index
      // in its place yet.
      { name: "robots", content: "noindex" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TermsRoute,
});

function TermsRoute() {
  const { t } = useTranslation();
  const terms = currentTerms();

  return (
    <main className="min-h-svh bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-2xl space-y-8">
        <header className="space-y-3">
          <Link
            to="/"
            search={{ enter: undefined, room: undefined }}
            aria-label={t("app.home")}
            className="flex items-center gap-2 text-foreground"
          >
            <Flame className="h-6 w-6 text-primary" aria-hidden="true" />
            <span className="text-lg font-semibold tracking-tight">
              {t("app.name")}
            </span>
          </Link>
        </header>

        <TermsDocument terms={terms} />

        <p>
          <Link
            to="/"
            search={{ enter: undefined, room: undefined }}
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
          >
            {t("action.back")}
          </Link>
        </p>
      </div>
    </main>
  );
}
